import { useMutation } from '@tanstack/react-query';
import { useApiClient } from '@core/api/useApiClient';
import { signOutToLogin } from '@core/auth/signOut';

interface PendingApprovalPageProps {
  status: 'pending' | 'rejected';
}

export function PendingApprovalPage({ status }: PendingApprovalPageProps) {
  const api = useApiClient();
  const reRequest = useMutation({
    mutationFn: () => api.post<void>('/api/v1/me/request-approval'),
  });

  const title = status === 'rejected' ? 'Account not approved' : 'Awaiting approval';
  const message =
    status === 'rejected'
      ? 'Your account has been rejected. You can request a new approval link to ask an administrator to review your registration again.'
      : 'Your account is awaiting admin approval. You will get access as soon as an administrator approves your registration.';

  const sent = reRequest.isSuccess;

  return (
    <div
      className="bg-canvas text-text"
      style={{ position: 'fixed', inset: 0, display: 'flex', alignItems: 'center', justifyContent: 'center' }}
    >
      <div className="w-full max-w-md px-6 text-center">
        <h1 className="mb-3 text-xl font-semibold">{title}</h1>
        <p className="mb-6 text-neutral-500">{message}</p>

        {status === 'rejected' ? (
          <>
            <button
              type="button"
              onClick={() => reRequest.mutate()}
              disabled={reRequest.isPending || sent}
              className="rounded-md bg-accent px-4 py-2 text-sm font-medium text-white disabled:opacity-50"
            >
              {reRequest.isPending ? 'Sending…' : sent ? 'Request sent' : 'Request a new approval link'}
            </button>
            {sent && (
              <p className="mt-3 text-sm text-neutral-500">
                A new request was sent. You&rsquo;ll get access once an administrator approves it.
              </p>
            )}
            {reRequest.isError && (
              <p className="mt-3 text-sm text-loss">
                {reRequest.error instanceof Error ? reRequest.error.message : 'Could not send the request. Please try again.'}
              </p>
            )}
          </>
        ) : (
          <>
            <button
              type="button"
              disabled
              className="rounded-md bg-accent px-4 py-2 text-sm font-medium text-white opacity-50"
            >
              Request a new approval link
            </button>
            <p className="mt-3 text-sm text-neutral-500">
              You already have a pending request awaiting approval.
            </p>
          </>
        )}

        {/* Always offer a way out — a stuck Pending/Rejected user can return to the login screen
            (sign out) to try a different account. */}
        <div className="mt-8 border-t border-neutral-500/15 pt-5">
          <button
            type="button"
            onClick={signOutToLogin}
            className="text-sm text-neutral-500 underline underline-offset-4 hover:text-text"
          >
            Sign out
          </button>
        </div>
      </div>
    </div>
  );
}
